/**
 * Error Normalizer
 *
 * This utility normalizes errors from various sources (Apollo GraphQL, Mandark REST,
 * generic JavaScript errors) into a consistent `AppError` shape that can be used
 * uniformly across the application for UI display and logging.
 *
 * @see docs/src/getting-started/error-handling-normalization.md
 * @see docs/src/getting-started/error-handling-plan.md
 *
 * ## Quick Start
 *
 * For most use cases, use `normalizeError()` - it auto-detects the error type:
 *
 * ```ts
 * import {normalizeError} from 'lib/normalizeError';
 *
 * try {
 *   await someApiCall();
 * } catch (error) {
 *   const appError = normalizeError(error);
 *   addToast({
 *     title: 'Error',
 *     color: 'negative',
 *     message: appError.message
 *   });
 * }
 * ```
 *
 * ## Primary Functions (Use These)
 *
 * | Function | When to Use |
 * |----------|-------------|
 * | `normalizeError(error)` | **Most common** - auto-detects error type and normalizes |
 * | `hasErrorCode(error, code)` | Check if error matches a specific `errorCodes` constant |
 * | `extractMandarkErrorDetail(error)` | Get the first error detail string for display |
 * | `shouldReportError(appError)` | Decide if error should be sent to Honeybadger |
 *
 * ## Checking for Specific Error Codes
 *
 * ```ts
 * import {hasErrorCode} from 'lib/normalizeError';
 * import errorCodes from 'client/errorCodes';
 *
 * // Before (verbose)
 * if (err.response?.body.errors[0].code === errorCodes.FORBIDDEN.EXCEEDED_HARD_CAP_LIMIT) { ... }
 *
 * // After (cleaner)
 * if (hasErrorCode(err, errorCodes.FORBIDDEN.EXCEEDED_HARD_CAP_LIMIT)) { ... }
 * ```
 *
 * ## Handling Validation Errors with Field Info
 *
 * ```ts
 * import {normalizeError, extractMandarkValidationErrors} from 'lib/normalizeError';
 *
 * const appError = normalizeError(error);
 *
 * if (appError.kind === 'validation') {
 *   // Option 1: Use the first field from appError
 *   if (appError.field === 'email') {
 *     setEmailError(appError.detail);
 *   }
 *
 *   // Option 2: Get all validation errors as a map
 *   const fieldErrors = extractMandarkValidationErrors(error);
 *   // { email: 'Email is invalid', password: 'Password too short' }
 * }
 * ```
 *
 * ## Error Kinds and What They Mean
 *
 * | Kind | HTTP Status | Typical Cause |
 * |------|-------------|---------------|
 * | `network` | N/A | No internet, server unreachable |
 * | `unauthorized` | 401 | Session expired, not logged in |
 * | `forbidden` | 403 | No permission for this action |
 * | `notFound` | 404 | Resource doesn't exist |
 * | `validation` | 400, 422 | Invalid input data |
 * | `rateLimited` | 429 | Too many requests |
 * | `timeout` | 408, 504 | Request took too long |
 * | `serverError` | 500+ | Backend error |
 * | `unknown` | Other | Unrecognized error |
 *
 * ## Severity Levels
 *
 * | Severity | Error Kinds | Honeybadger | UI Treatment |
 * |----------|-------------|-------------|--------------|
 * | `low` | validation | No auto-report | Simple toast |
 * | `medium` | notFound, rateLimited, timeout | Auto-report | Toast with retry |
 * | `high` | network, unauthorized, forbidden, serverError, unknown | Auto-report | Error boundary |
 *
 * ## Advanced: Helper Functions
 *
 * These are exported for edge cases but rarely needed directly:
 *
 * - `normalizeApolloError(error)` - Only for Apollo errors
 * - `normalizeMandarkError(error)` - Only for Mandark REST errors
 * - `isApolloError(error)` - Type guard
 * - `isMandarkError(error)` - Type guard
 * - `isNetworkError(error)` - Type guard
 * - `getKindFromStatus(status)` - Map HTTP status to ErrorKind
 * - `getKindFromCode(code)` - Map API error code to ErrorKind
 * - `getSeverityFromKind(kind)` - Get default severity for a kind
 * - `getDefaultMessage(kind)` - Get user-friendly message for a kind
 * - `getFirstErrorCode(error)` - Extract first error code
 * - `getAllMandarkErrors(error)` - Get all errors as array
 * - `createAppError(overrides)` - Factory for custom AppError
 * - `isAppError(error)` - Type guard for AppError
 * - `isErrorKind(error, kind)` - Check if error is of specific kind
 */

/**
 * Error severity level constants
 */
export const ERROR_SEVERITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high'
} as const;

/**
 * Error kind constants for type-safe comparisons
 *
 * @example
 * ```ts
 * if (appError.kind === ERROR_KIND.VALIDATION) {
 *   // handle validation error
 * }
 * ```
 */
export const ERROR_KIND = {
  NETWORK: 'network',
  UNAUTHORIZED: 'unauthorized',
  FORBIDDEN: 'forbidden',
  NOT_FOUND: 'notFound',
  VALIDATION: 'validation',
  RATE_LIMITED: 'rateLimited',
  TIMEOUT: 'timeout',
  SERVER_ERROR: 'serverError',
  UNKNOWN: 'unknown'
} as const;

/**
 * Centralized configuration for each error kind.
 * Single source of truth for default messages and severity levels.
 *
 * When adding a new error kind, add its config entry here and
 * everything else (messages, severity, defaults) will be derived automatically.
 */
export const ERROR_KIND_CONFIG = {
  [ERROR_KIND.NETWORK]: {
    severity: ERROR_SEVERITY.HIGH,
    message: 'Unable to connect. Please check your internet connection and try again.'
  },
  [ERROR_KIND.UNAUTHORIZED]: {
    severity: ERROR_SEVERITY.HIGH,
    message: 'Your session has expired. Please sign in again.'
  },
  [ERROR_KIND.FORBIDDEN]: {
    severity: ERROR_SEVERITY.HIGH,
    message: "You don't have permission to perform this action."
  },
  [ERROR_KIND.NOT_FOUND]: {
    severity: ERROR_SEVERITY.MEDIUM,
    message: 'The requested resource could not be found.'
  },
  [ERROR_KIND.VALIDATION]: {
    severity: ERROR_SEVERITY.LOW,
    message: 'Please check your input and try again.'
  },
  [ERROR_KIND.RATE_LIMITED]: {
    severity: ERROR_SEVERITY.MEDIUM,
    message: 'Too many requests. Please wait a moment and try again.'
  },
  [ERROR_KIND.TIMEOUT]: {
    severity: ERROR_SEVERITY.MEDIUM,
    message: 'The request timed out. Please try again.'
  },
  [ERROR_KIND.SERVER_ERROR]: {
    severity: ERROR_SEVERITY.HIGH,
    message: 'Something went wrong on our end. Please try again later.'
  },
  [ERROR_KIND.UNKNOWN]: {
    severity: ERROR_SEVERITY.HIGH,
    message: 'An unexpected error occurred. Please try again.'
  }
} as const satisfies Record<ErrorKind, {severity: ErrorSeverity; message: string}>;

/** Default user-friendly messages for each error kind (derived from ERROR_KIND_CONFIG) */
export const ERROR_MESSAGES = Object.fromEntries(
  Object.entries(ERROR_KIND_CONFIG).map(([kind, config]) => [kind, config.message])
) as {readonly [K in ErrorKind]: string};

/** Default severity for each error kind (derived from ERROR_KIND_CONFIG) */
export const ERROR_KIND_SEVERITY = Object.fromEntries(
  Object.entries(ERROR_KIND_CONFIG).map(([kind, config]) => [kind, config.severity])
) as {readonly [K in ErrorKind]: ErrorSeverity};

/**
 * HTTP status code to error kind mapping
 */
export const STATUS_CODE_MAP: Record<number, ErrorKind> = {
  401: ERROR_KIND.UNAUTHORIZED,
  403: ERROR_KIND.FORBIDDEN,
  404: ERROR_KIND.NOT_FOUND,
  408: ERROR_KIND.TIMEOUT,
  422: ERROR_KIND.VALIDATION,
  429: ERROR_KIND.RATE_LIMITED,
  504: ERROR_KIND.TIMEOUT
} as const;

export type ErrorSeverity = (typeof ERROR_SEVERITY)[keyof typeof ERROR_SEVERITY];

export type ErrorKind = (typeof ERROR_KIND)[keyof typeof ERROR_KIND];

export interface AppError {
  /** Categorized error type for UI handling */
  kind: ErrorKind;
  /** Severity level for logging/UI decisions */
  severity: ErrorSeverity;
  /** HTTP status code if available */
  status?: number;
  /** Structured error code from the API (e.g., 'BAD_REQUEST.VALIDATION_ERROR') */
  code?: string;
  /** User-friendly error message */
  message: string;
  /** Detailed error message (may not be user-friendly) */
  detail?: string;
  /** Field name if this is a field-specific validation error */
  field?: string;
  /** Original error for debugging/Honeybadger context */
  originalError: unknown;
}

/**
 * GraphQL error shape from Apollo Client
 */
interface GraphQLError {
  message: string;
  locations?: Array<{line: number; column: number}>;
  path?: Array<string | number>;
  extensions?: {
    code?: string;
    [key: string]: unknown;
  };
}

/**
 * Apollo-like error structure
 */
interface ApolloErrorLike {
  message: string;
  graphQLErrors?: GraphQLError[];
  networkError?: Error & {
    statusCode?: number;
    result?: {
      errors?: Array<{message: string; code?: string}>;
    };
  };
  cause?: unknown;
}

/**
 * Mandark REST error shape (JSON:API format)
 */
interface MandarkError {
  code?: string;
  title?: string;
  detail?: string;
  status?: number | string;
  source?: {
    parameter?: string;
    pointer?: string;
  };
  meta?: Record<string, unknown>;
}

/**
 * Mandark REST response error structure
 */
interface MandarkResponseError {
  response?: {
    body?: {
      errors?: MandarkError[];
    };
    status?: number;
  };
  status?: number;
  message?: string;
}

/**
 * Maps HTTP status codes to error kinds
 */
export function getKindFromStatus(status: number): ErrorKind {
  // Check exact matches first
  if (status in STATUS_CODE_MAP) {
    return STATUS_CODE_MAP[status];
  }
  // Fallback for ranges
  if (status >= 500) return ERROR_KIND.SERVER_ERROR;
  if (status >= 400) return ERROR_KIND.VALIDATION;
  return ERROR_KIND.UNKNOWN;
}

/**
 * Maps error kinds to default severity levels
 */
export function getSeverityFromKind(kind: ErrorKind): ErrorSeverity {
  return ERROR_KIND_SEVERITY[kind] ?? ERROR_SEVERITY.MEDIUM;
}

/**
 * Maps error codes to error kinds (for structured API error codes)
 */
export function getKindFromCode(code?: string): ErrorKind | null {
  if (!code) return null;

  const normalizedCode = code.toUpperCase();

  if (normalizedCode.includes('UNAUTHENTICATED') || normalizedCode.includes('UNAUTHORIZED')) {
    return ERROR_KIND.UNAUTHORIZED;
  }
  if (normalizedCode.includes('FORBIDDEN') || normalizedCode.includes('ACCESS_DENIED')) {
    return ERROR_KIND.FORBIDDEN;
  }
  if (normalizedCode.includes('NOT_FOUND') || normalizedCode.includes('NOTFOUND')) {
    return ERROR_KIND.NOT_FOUND;
  }
  if (
    normalizedCode.includes('VALIDATION') ||
    normalizedCode.includes('BAD_REQUEST') ||
    normalizedCode.includes('BADREQUEST')
  ) {
    return ERROR_KIND.VALIDATION;
  }
  if (normalizedCode.includes('RATE_LIMIT') || normalizedCode.includes('TOO_MANY_REQUESTS')) {
    return ERROR_KIND.RATE_LIMITED;
  }
  if (normalizedCode.includes('TIMEOUT')) {
    return ERROR_KIND.TIMEOUT;
  }
  if (normalizedCode.includes('INTERNAL') || normalizedCode.includes('SERVER_ERROR')) {
    return ERROR_KIND.SERVER_ERROR;
  }

  return null;
}

/**
 * Provides user-friendly default messages for each error kind
 */
export function getDefaultMessage(kind: ErrorKind): string {
  return ERROR_MESSAGES[kind] ?? ERROR_MESSAGES[ERROR_KIND.UNKNOWN];
}

/**
 * Creates an AppError with consistent defaults
 * Automatically derives severity from kind and message from kind if not provided
 */
export function createAppError(overrides: Partial<AppError> & {originalError: unknown}): AppError {
  const kind = overrides.kind || ERROR_KIND.UNKNOWN;
  return {
    kind,
    severity: overrides.severity || getSeverityFromKind(kind),
    message: overrides.message || getDefaultMessage(kind),
    ...overrides
  };
}

/**
 * Checks if an error looks like an Apollo error
 */
export function isApolloError(error: unknown): error is ApolloErrorLike {
  if (!error || typeof error !== 'object') return false;
  const obj = error as Record<string, unknown>;
  return (
    Array.isArray(obj.graphQLErrors) ||
    obj.networkError !== undefined ||
    (typeof obj.message === 'string' && 'cause' in obj)
  );
}

/**
 * Checks if an error looks like a Mandark REST error
 */
export function isMandarkError(error: unknown): error is MandarkResponseError {
  if (!error || typeof error !== 'object') return false;
  const obj = error as Record<string, unknown>;
  const response = obj.response as Record<string, unknown> | undefined;
  return !!(
    response?.body &&
    typeof response.body === 'object' &&
    Array.isArray((response.body as Record<string, unknown>).errors)
  );
}

/**
 * Checks if the error is a network-related error
 */
export function isNetworkError(error: unknown): boolean {
  if (!error) return false;

  if (error instanceof TypeError && error.message.toLowerCase().includes('network')) {
    return true;
  }

  if (typeof error === 'object') {
    const obj = error as Record<string, unknown>;

    // Check for fetch/network failure indicators
    if (obj.name === 'TypeError' || obj.name === 'NetworkError') {
      return true;
    }

    // Check for Apollo network error
    if (obj.networkError) {
      return true;
    }

    const message = typeof obj.message === 'string' ? obj.message.toLowerCase() : '';
    if (
      message.includes('network') ||
      message.includes('failed to fetch') ||
      message.includes('load failed') ||
      message.includes('net::')
    ) {
      return true;
    }
  }

  return false;
}

/**
 * Normalizes Apollo/GraphQL errors into AppError
 */
export function normalizeApolloError(error: ApolloErrorLike): AppError {
  // Handle network errors first
  if (error.networkError) {
    const status = error.networkError.statusCode;

    // If we have a status code, it might be an HTTP error wrapped as network error
    if (status) {
      return createAppError({
        kind: getKindFromStatus(status),
        status,
        detail: error.networkError.message,
        originalError: error
      });
    }

    // True network failure (no response)
    return createAppError({
      kind: ERROR_KIND.NETWORK,
      detail: error.networkError.message,
      originalError: error
    });
  }

  // Handle GraphQL errors
  if (error.graphQLErrors && error.graphQLErrors.length > 0) {
    const firstError = error.graphQLErrors[0];
    const code = firstError.extensions?.code as string | undefined;
    const kindFromCode = getKindFromCode(code);

    return createAppError({
      kind: kindFromCode || ERROR_KIND.UNKNOWN,
      code,
      detail: firstError.message,
      originalError: error
    });
  }

  // Fallback for other Apollo errors
  return createAppError({
    kind: ERROR_KIND.UNKNOWN,
    message: error.message || undefined,
    originalError: error
  });
}

/**
 * Normalizes Mandark REST/JSON:API errors into AppError
 */
export function normalizeMandarkError(error: MandarkResponseError): AppError {
  const response = error.response;
  const status = response?.status || error.status;
  const errors = response?.body?.errors || [];
  const firstError = errors[0];

  if (firstError) {
    const code = firstError.code;
    const kindFromCode = getKindFromCode(code);
    const kindFromStatus = status ? getKindFromStatus(status) : null;
    const field = firstError.source?.parameter || firstError.source?.pointer;

    return createAppError({
      kind: kindFromCode || kindFromStatus || ERROR_KIND.UNKNOWN,
      status:
        typeof firstError.status === 'string' ? parseInt(firstError.status, 10) || status : status,
      code,
      detail: firstError.detail || firstError.title,
      field,
      originalError: error
    });
  }

  // No structured errors, try to determine from status
  if (status) {
    return createAppError({
      kind: getKindFromStatus(status),
      status,
      detail: error.message,
      originalError: error
    });
  }

  return createAppError({
    kind: ERROR_KIND.UNKNOWN,
    message: error.message || undefined,
    originalError: error
  });
}

/**
 * Normalizes any error into AppError
 *
 * This is the main entry point that automatically detects the error type
 * and delegates to the appropriate normalizer.
 *
 * @example
 * ```ts
 * try {
 *   await someApiCall();
 * } catch (error) {
 *   const appError = normalizeError(error);
 *   showErrorToast(appError);
 * }
 * ```
 */
export function normalizeError(error: unknown): AppError {
  // Handle null/undefined
  if (error == null) {
    return createAppError({
      kind: ERROR_KIND.UNKNOWN,
      severity: ERROR_SEVERITY.LOW,
      originalError: error
    });
  }

  // Handle string errors
  if (typeof error === 'string') {
    return createAppError({
      kind: ERROR_KIND.UNKNOWN,
      severity: ERROR_SEVERITY.LOW,
      message: error || undefined,
      detail: error,
      originalError: error
    });
  }

  // Check for network errors first (they can masquerade as other types)
  if (isNetworkError(error)) {
    return createAppError({
      kind: ERROR_KIND.NETWORK,
      detail: error instanceof Error ? error.message : String(error),
      originalError: error
    });
  }

  // Handle Apollo errors
  if (isApolloError(error)) {
    return normalizeApolloError(error);
  }

  // Handle Mandark REST errors
  if (isMandarkError(error)) {
    return normalizeMandarkError(error);
  }

  // Handle objects with status (generic HTTP-like errors)
  if (typeof error === 'object') {
    const obj = error as Record<string, unknown>;

    // Check for status code
    const status =
      typeof obj.status === 'number'
        ? obj.status
        : typeof obj.statusCode === 'number'
          ? obj.statusCode
          : undefined;

    if (status) {
      return createAppError({
        kind: getKindFromStatus(status),
        status,
        detail: typeof obj.message === 'string' ? obj.message : undefined,
        originalError: error
      });
    }

    // Check for code field
    const code = typeof obj.code === 'string' ? obj.code : undefined;
    if (code) {
      return createAppError({
        kind: getKindFromCode(code) || ERROR_KIND.UNKNOWN,
        code,
        detail: typeof obj.message === 'string' ? obj.message : undefined,
        originalError: error
      });
    }
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return createAppError({
      kind: ERROR_KIND.UNKNOWN,
      detail: error.message,
      originalError: error
    });
  }

  // Fallback for anything else
  return createAppError({
    kind: ERROR_KIND.UNKNOWN,
    severity: ERROR_SEVERITY.LOW,
    detail: String(error),
    originalError: error
  });
}

/**
 * Extracts the first error detail from a Mandark REST response
 * Useful for validation errors where you want to show a specific field error
 */
export function extractMandarkErrorDetail(error: unknown): string | null {
  if (!isMandarkError(error)) return null;

  const firstError = error.response?.body?.errors?.[0];
  return firstError?.detail || null;
}

/**
 * Extracts all validation errors from a Mandark REST response
 * Returns a map of field name to error message
 */
export function extractMandarkValidationErrors(error: unknown): Record<string, string> | null {
  if (!isMandarkError(error)) return null;

  const errors = error.response?.body?.errors || [];
  const result: Record<string, string> = {};

  for (const err of errors) {
    const field = err.source?.parameter || err.source?.pointer || 'general';
    result[field] = err.detail || err.title || 'Invalid value';
  }

  return Object.keys(result).length > 0 ? result : null;
}

/**
 * Checks if an error is of a specific kind
 */
export function isErrorKind(error: unknown, kind: ErrorKind): boolean {
  const normalized = normalizeError(error);
  return normalized.kind === kind;
}

/**
 * Checks if an error should be reported to Honeybadger based on severity
 */
export function shouldReportError(error: AppError): boolean {
  // Only report medium and high severity errors automatically
  return error.severity === 'medium' || error.severity === 'high';
}

/**
 * Checks if a Mandark error has a specific error code
 * Useful for matching against errorCodes constants
 *
 * @example
 * ```ts
 * if (hasErrorCode(error, errorCodes.FORBIDDEN.EXCEEDED_HARD_CAP_LIMIT)) {
 *   // handle hard cap limit error
 * }
 * ```
 */
export function hasErrorCode(error: unknown, expectedCode: string): boolean {
  if (!error || typeof error !== 'object') return false;

  // Check direct code property (for extracted error objects)
  const obj = error as Record<string, unknown>;
  if (obj.code === expectedCode) return true;

  // Check Mandark response structure
  if (isMandarkError(error)) {
    const errors = error.response?.body?.errors || [];
    return errors.some((err) => err.code === expectedCode);
  }

  // Check Apollo GraphQL errors
  if (isApolloError(error)) {
    return (
      error.graphQLErrors?.some((gqlError) => gqlError.extensions?.code === expectedCode) ?? false
    );
  }

  return false;
}

/**
 * Gets the first error code from a Mandark or Apollo error
 */
export function getFirstErrorCode(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null;

  // Check direct code property
  const obj = error as Record<string, unknown>;
  if (typeof obj.code === 'string') return obj.code;

  // Check Mandark response structure
  if (isMandarkError(error)) {
    return error.response?.body?.errors?.[0]?.code || null;
  }

  // Check Apollo GraphQL errors
  if (isApolloError(error)) {
    const code = error.graphQLErrors?.[0]?.extensions?.code;
    return typeof code === 'string' ? code : null;
  }

  return null;
}

/**
 * Gets all errors from a Mandark response as an array of MandarkError objects
 * Useful when you need to process multiple validation errors
 */
export function getAllMandarkErrors(error: unknown): MandarkError[] {
  if (!isMandarkError(error)) return [];
  return error.response?.body?.errors || [];
}

/**
 * Type guard to check if an error is an AppError
 */
export function isAppError(error: unknown): error is AppError {
  if (!error || typeof error !== 'object') return false;
  const obj = error as Record<string, unknown>;
  return (
    typeof obj.kind === 'string' &&
    typeof obj.severity === 'string' &&
    typeof obj.message === 'string' &&
    'originalError' in obj
  );
}

// Re-export types for external use
export type {ApolloErrorLike, GraphQLError, MandarkError, MandarkResponseError};
